import { CustomColorPickerPopover } from "components/colorpicker";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { cloneDeep } from "lodash";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { WebsiteBuilderRange } from "pages/secure/facility/websiteBuilder/editor/components/range/WebsiteBuilderRange";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import {
  IWebsiteBuilderBoxSettings,
  WEBSITE_BUILDER_BOX_MIN_BORDER_RADIUS,
  WEBSITE_BUILDER_BOX_MAX_BORDER_RADIUS,
} from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderBox";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { websiteBuilderUpdateContainerSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";
import { WebsiteBuilderHoverSettings } from "../../../../../helpers/WebsiteBuilderHoverSettings";

interface IBoxSettingsProps {
  id: string;
  settings: IWebsiteBuilderBoxSettings;
  closeSettings: () => void;
}

export function BoxSettings(props: IBoxSettingsProps) {
  const { id, settings, closeSettings } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderBoxSettings>(() => cloneDeep(settings));

  function dispatchSettings(prevSettings: IWebsiteBuilderBoxSettings, nextSettings: IWebsiteBuilderBoxSettings) {
    dispatch(
      websiteBuilderUpdateContainerSettings(activePageId, deviceView, id, {
        id,
        prevContainerSettings: {
          type: "box",
          settings: cloneDeep(prevSettings),
        },
        nextContainerSettings: {
          type: "box",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function handleChangeBorderRadius(borderRadius: number) {
    handleDebouncedUpdateState(prev => ({
      ...prev,
      borderRadius,
    }));
  }

  function renderSettings() {
    return (
      <>
        <WebsiteBuilderRange
          label={t("Border radius (px)")}
          min={WEBSITE_BUILDER_BOX_MIN_BORDER_RADIUS}
          max={WEBSITE_BUILDER_BOX_MAX_BORDER_RADIUS}
          step={1}
          value={updatedSettings.borderRadius}
          setValue={handleChangeBorderRadius}
        />
        <CustomColorPickerPopover
          title={t("Background")}
          hexColor={updatedSettings.backgroundColor}
          updateHexColor={backgroundColor =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              backgroundColor,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
        <WebsiteBuilderHoverSettings>
          <CustomColorPickerPopover
            title={t("Background")}
            hexColor={updatedSettings.hoverBackgroundColor}
            updateHexColor={hoverBackgroundColor =>
              handleDebouncedUpdateState(prev => ({
                ...prev,
                hoverBackgroundColor,
              }))
            }
            portalParentId={WEBSITE_BUILDER_PORTAL}
          />
        </WebsiteBuilderHoverSettings>
      </>
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Box settings")}
      closePanel={closeSettings}
      sections={[renderSettings()]}
    />
  );
}
